import React, { useState, useCallback } from 'react';
import {
  LeadsFormWrapper,
  LeadsFormHead,
  LeadsFormImages,
  LeadsFormTitle,
  LeadsFormSubtitle,
  LeadsFormContainer,
  LeadsForm,
  FormField,
  StyledLabel,
  StyledInput,
  FormFieldCheckbox,
  CheckboxInput,
  CheckboxLabel,
  Button,
  ErrorMessage,
  LeadsFormHeaderImage,
} from './LeadsFormCapture.style';
import config from '../../common/config';
import LeadsHeaderMobileImage from '../../common/assets/LeadsHeadImage2.png';
import {
  fetchToken,
  handleLeadsSubmit,
} from '../../services/ProjectReviewsArticleLead';
import ContentLeadCapture from '../../locales/th/leadcapture.json';

interface LeadsFormCaptureProps {
  leadCaptureFormId: string;
  projectId?: number;
  updateSuccessPopup: (value: boolean) => void;
  articleId?: string;
  onClickRegisterButton?: () => void;
}
interface FormData {
  firstName: string;
  mobileNum: string;
  isChecked: boolean;
}
interface FormErrors {
  firstName: string | null;
  mobileNum: string | null;
  isChecked: string | null;
}

export const LeadsFormCapture: React.FC<LeadsFormCaptureProps> = ({
  projectId,
  leadCaptureFormId,
  updateSuccessPopup,
}) => {
  const [loading, isLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    mobileNum: '',
    isChecked: false,
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({
    firstName: null,
    mobileNum: null,
    isChecked: null,
  });
  const formatMobileNumber = (value: string) => {
    const cleaned = ('' + value).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return match[1] + '-' + match[2] + '-' + match[3];
    }
    return value;
  };

  const submitLeadForm = useCallback(
    async (token: string) => {
      try {
        const result = await handleLeadsSubmit(
          formData,
          token,
          config,
          projectId,
          leadCaptureFormId,
        );
        if ('error' in result) {
          console.error('Error submitting form:', result.error);
        } else if (
          'responseData' in result &&
          result.responseData &&
          'error' in result.responseData
        ) {
          isLoading(false);
          console.error(
            'Error submitting form:',
            (result?.responseData as { error?: string })?.error,
          );
        } else if (
          'responseData' in result &&
          result.responseData &&
          'formId' in result.responseData &&
          (result.responseData as { formId?: string }).formId
        ) {
          isLoading(false);
          updateSuccessPopup(true);
          setFormData({
            firstName: '',
            mobileNum: '',
            isChecked: false,
          });
        } else {
          console.error('Error submitting form');
        }
      } catch (error) {
        isLoading(false);
        console.error('Error submitting form:', error);
      }
    },
    [formData, projectId, leadCaptureFormId, updateSuccessPopup],
  );

  const handleSubmit = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      const errors: FormErrors = {
        firstName: !formData.firstName.trim()
          ? ContentLeadCapture.formErrors.invalidFirstName
          : null,
        mobileNum: !formData.mobileNum.trim()
          ? ContentLeadCapture.formErrors.invalidMobileNum
          : null,
        isChecked: !formData.isChecked
          ? ContentLeadCapture.formErrors.invalidChecked
          : null,
      };
      const phoneNumberPattern = /^(02|04|06|08|09)\d{8}$/; // Matches numbers starting with '02', '04', or '09' followed by 8 digits
      if (
        formData.mobileNum.trim() &&
        !phoneNumberPattern.test(formData.mobileNum.trim())
      ) {
        errors.mobileNum = ContentLeadCapture.formErrors.invalidMobileFormat;
      }
      setFormErrors(errors);

      if (!errors.firstName && !errors.mobileNum && !errors.isChecked) {
        isLoading(true);
        try {
          const token: string = await fetchToken(config);
          submitLeadForm(token);
        } catch (error) {
          isLoading(false);
          console.error('Error:', error);
        }
      }
    },
    [
      formData.firstName,
      formData.isChecked,
      formData.mobileNum,
      submitLeadForm,
    ],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      if (name === 'mobileNum') {
        const numericValue = value.replace(/\D/g, '');
        setFormData((prevFormData) => ({
          ...prevFormData,
          [name]: numericValue,
        }));
      } else {
        setFormData((prevFormData) => ({
          ...prevFormData,
          [name]: value,
        }));
      }
    },
    [],
  );
  const handleCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = e.target;
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: checked,
      }));
    },
    [],
  );

  return (
    <LeadsFormWrapper data-testid="leads-form-capture">
      <LeadsFormHead>
        <LeadsFormImages>
          <LeadsFormHeaderImage src={LeadsHeaderMobileImage} alt={'image'} />
        </LeadsFormImages>
        <LeadsFormTitle>{ContentLeadCapture.title}</LeadsFormTitle>
        <LeadsFormSubtitle>{ContentLeadCapture.subTitle}</LeadsFormSubtitle>
      </LeadsFormHead>
      <div>
        <LeadsFormContainer>
          <LeadsForm>
            <FormField>
              <StyledLabel>{ContentLeadCapture.form.firstName}</StyledLabel>
              <StyledInput
                type="text"
                id="firstName"
                name="firstName"
                placeholder={ContentLeadCapture.form.firstName}
                isError={!!formErrors.firstName}
                value={formData.firstName}
                onChange={handleInputChange}
              />
              {formErrors.firstName && (
                <ErrorMessage>{formErrors.firstName}</ErrorMessage>
              )}
            </FormField>
            <FormField>
              <StyledLabel>{ContentLeadCapture.form.mobileNum}</StyledLabel>
              <StyledInput
                type="tel"
                id="mobileNum"
                name="mobileNum"
                maxLength={10}
                placeholder={ContentLeadCapture.form.mobilePlaceholder}
                isError={!!formErrors.mobileNum}
                value={formatMobileNumber(formData.mobileNum)}
                onChange={handleInputChange}
              />
              {formErrors.mobileNum && (
                <ErrorMessage>{formErrors.mobileNum}</ErrorMessage>
              )}
            </FormField>
            <FormFieldCheckbox>
              <CheckboxInput
                type="checkbox"
                name="isChecked"
                id="agree"
                isError={!!formErrors.isChecked}
                checked={formData.isChecked}
                onChange={handleCheckboxChange}
              />
              <CheckboxLabel
                htmlFor="agree"
                dangerouslySetInnerHTML={{
                  __html: ContentLeadCapture.form.checkboxLabel,
                }}
              />
            </FormFieldCheckbox>
            <Button
              type="submit"
              data-testid="register-button"
              disabled={loading}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                handleSubmit(e)
              }
            >
              {loading
                ? ContentLeadCapture.form.buttonLoadingLabel
                : ContentLeadCapture.form.buttonLabel}
            </Button>
          </LeadsForm>
        </LeadsFormContainer>
      </div>
    </LeadsFormWrapper>
  );
};
